import TopMenu from '../TopMenu/TopMenu';
import RoomSceneList from "../RoomSceneList/RoomSceneList";
import styles from "./RoomViewPage.module.css";
import { useParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import ChamUpload, { ChamUploadButton } from '../ChamUpload/ChamUpload';

import ShareModal from '../ShareModal/ShareModal';
import EmailShareModal from '../EmailShareModal/EmailShareModal';
import HelpModal from '../HelpModal/HelpModal';
import FeedbackModal from '../FeedbackModal/FeedbackModal';
import PatternSizeSelectorModal from '../PatternSizeSelectorModal/PatternSizeSelectorModal';

export default function ViewInRoomPage() {

    const urlParams = useParams();

    const activePattern = useSelector(state => state.visualizer.activePattern);
    const selectedSlotProducts = useSelector(state => state.visualizer.selectedSlotProducts);

    const shareModalStatus = useSelector((state) => state.ui.shareModalActive);
    const emailModalStatus = useSelector((state) => state.ui.emailModalActive);
    const helpModalStatus = useSelector((state) => state.ui.helpModalActive);
    const feedbackModalStatus = useSelector((state) => state.ui.feedbackModalActive);
    const howToDesignModalStatus = useSelector((state) => state.ui.PatternSizeSelectorModalActive);

    const [isImageLoading, setIsImageLoading] = useState(true);
    const [currentSpecifier, setCurrentSpecifier] = useState(urlParams.spec);
    const [filters, setFilters] = useState([]);
    const [activeFilter, setActiveFilter] = useState('All');

    // Fetch room scene data to populate filters
    useEffect(() => {
        const URL = 'https://lizard.chameleonpower.com/CDN/RoomScenes/9705.json';
        fetch(URL)
            .then((response) => response.json())
            .then((data) => {
                // Extract unique descriptions for filters
                const descriptions = [...new Set(data.map(scene => scene.description).filter(desc => desc))];
                setFilters(['All', ...descriptions.sort()]);
            })
            .catch((error) => {
                console.error('Error fetching room scenes:', error);
            });
    }, []);

    // Generate the sel parameter for room view
    const generateRoomSelParameter = () => {
        // First check if selection is in URL params
        if (urlParams.selection) {
            return urlParams.selection;
        }
        
        if (!activePattern) {
            return '';
        }

        // Start with pattern ID
        const patternId = activePattern.id || activePattern.patternId || '';
        
        // Get selected product IDs
        const productIds = [];
        if (activePattern.descriptor) {
            const colorSlots = activePattern.descriptor.split(',').map(slot => slot.trim());
            colorSlots.forEach((_, index) => {
                const product = selectedSlotProducts[index];
                if (product && product.id) {
                    productIds.push(product.id);
                } else {
                    productIds.push('-1');
                }
            });
        }

        // Combine pattern ID with product IDs
        if (productIds.length > 0) {
            return `${patternId}(${productIds.join(',')})`;
        }
        
        return patternId;
    };

    // Generate initial room render URL with sel parameter
    const getInitialRoomUrl = () => {
        const selParam = generateRoomSelParameter();
        const baseUrl = `https://renderfarm1.chameleonpower.com/cham/getImage.ashx?spec=${urlParams.spec}&type=base&size=1024`;
        
        if (selParam) {
            return `${baseUrl}&sel=${encodeURIComponent(selParam)}`;
        }
        
        return baseUrl;
    };

    const [roomRender, setRoomRender] = useState(getInitialRoomUrl());

    // Handle scene selection from RoomSceneList
    const handleSceneSelect = (specifier) => {
        setCurrentSpecifier(specifier);
        setIsImageLoading(true);
        
        // Update the room image URL with new specifier and current sel parameter
        const selParam = generateRoomSelParameter();
        const baseUrl = `https://renderfarm1.chameleonpower.com/cham/getImage.ashx?spec=${encodeURIComponent(specifier)}&type=base&size=1024`;
        
        if (selParam) {
            setRoomRender(`${baseUrl}&sel=${encodeURIComponent(selParam)}`);
        } else {
            setRoomRender(baseUrl);
        }
    };

    // Handle image load completion
    const handleImageLoad = () => {
        setIsImageLoading(false);
    };

    // Handle image load error
    const handleImageError = () => {
        setIsImageLoading(false);
        console.error('Failed to load room image');
    };

    // Edit uploaded image
    const editImage = function() {
        window.location.href = "/im.aspx?spec="+encodeURI(currentSpecifier);
    }

    // Navigate back to visualizer with current pattern and products
    const handleEditPatternLink = function() {
        const { origin } = window.location;
        
        // Check if selection parameter exists in URL params
        if (urlParams.selection) {
            // Parse selection parameter: format is "patternId(productId1,productId2,productId3)"
            const selectionParam = urlParams.selection;
            
            // Extract pattern ID and product IDs using regex
            const match = selectionParam.match(/^(\d+)\(([^)]+)\)$/);
            
            if (match) {
                const patternId = match[1];
                const productIds = match[2];
                
                // Create redirect URL
                const redirectUrl = `${origin}/visualizer/pattern/${patternId}/products/${productIds}`;
                
                console.log('Redirecting to:', redirectUrl);
                
                // Navigate to the visualizer with the pattern and products
                window.location.href = redirectUrl;
            } else {
                console.error('Invalid selection parameter format:', selectionParam);
                // Fallback to visualizer home
                window.location.href = `${origin}/visualizer`;
            }
        } else {
            // No selection parameter, redirect to visualizer home
            console.log('No selection parameter found, redirecting to visualizer home');
            window.location.href = `${origin}/visualizer`;
        }
    }

    // Update room render URL when selected products change
    useEffect(() => {
        const newUrl = getInitialRoomUrl();
        if (newUrl !== roomRender) {
            setIsImageLoading(true);
            setRoomRender(newUrl);
        }
    }, [selectedSlotProducts, activePattern]);

    return (
        <>
        <TopMenu extended={true} />
        
        <div className={styles.content}>
            <div className={styles.splitContainer}>
                
                <div className={styles.vizImage}>
                    <div className={styles.flexDiv}>
                        {currentSpecifier.indexOf("_USER_") !== -1 && (
                            <button className={styles.editImage} onClick={editImage}>
                                Edit Image
                            </button>
                        )}
                    </div>
                    
                    <div className={styles.imageContainer}>
                        {isImageLoading && (
                            <div className={styles.imageLoadingOverlay}>
                                <div className={styles.imageLoadingSpinner}></div>
                                <div className={styles.imageLoadingMessage}>Loading room view...</div>
                            </div>
                        )}
                        <img 
                            id="roomViewMain" 
                            src={roomRender}
                            onLoad={handleImageLoad}
                            onError={handleImageError}
                            style={{ opacity: isImageLoading ? 0 : 1 }}
                            alt="Room view with pattern"
                        />
                    </div>
                    
                    <div className={styles.disclaimer}>
                        <span>Disclaimer:</span> Patterns in room scene renderings may not be to scale.
                    </div>
                </div>
                
                <div className={styles.scenesMenu}>
                    <div className={styles.scenesMenuActions}>
                        <button className={styles.editPattern} onClick={handleEditPatternLink}>
                            Edit Pattern
                        </button>
                        <ChamUploadButton title="Upload Roomscene" className={styles.uploadRoomScene} aria-label="Upload Button"  />
                    </div>
                    
                    <div className={styles.filterDropdownContainer}>
                        <label htmlFor="roomSceneFilter" className={styles.filterLabel}>Filter by Category:</label>
                        <select 
                            id="roomSceneFilter"
                            className={styles.filterDropdown}
                            value={activeFilter}
                            onChange={(e) => setActiveFilter(e.target.value)}
                        >
                            {filters.map(filter => (
                                <option key={filter} value={filter}>
                                    {filter}
                                </option>
                            ))}
                        </select>
                    </div>
                    
                    <div className={styles.scenesList}>
                        <RoomSceneList
                            seriesId="9705"
                            thumbnailSize="400"
                            onSceneSelect={handleSceneSelect}
                            filterDescription={activeFilter === 'All' ? null : activeFilter}
                        />
                    </div>
                </div>
            </div>
        </div>
        
        <ChamUpload/>
        
        {shareModalStatus && <ShareModal/>}
        {emailModalStatus && <EmailShareModal />}
        {helpModalStatus && <HelpModal />}
        {feedbackModalStatus && <FeedbackModal />}
        {howToDesignModalStatus && <PatternSizeSelectorModal />}
        </>
    )
}